import { useState, useEffect } from 'react';
import Button from '../../../components/Button';
import { Modal } from '../../../components/Modal';
import { Toggle } from '../../../components/Toggle';
import { referralCodesService, type CreateReferralCodeData, type ReferralCodeData, type UpdateReferralCodeData } from '../services/referralCodesService';

interface ReferralCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  code?: ReferralCodeData;
  onSuccess: (code: ReferralCodeData, isEdit: boolean) => void;
}

export const ReferralCodeModal = ({ isOpen, onClose, code, onSuccess }: ReferralCodeModalProps) => {
  const isEditMode = !!code;

  const [formCode, setFormCode] = useState('');
  const [discountPercent, setDiscountPercent] = useState(0);
  const [revenueSharePercent, setRevenueSharePercent] = useState(0);
  const [applyResultMessage, setApplyResultMessage] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [expiresAt, setExpiresAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Initialize form data when modal opens or code changes
  useEffect(() => {
    if (isOpen) {
      if (code) {
        // Edit mode - populate with existing data
        setFormCode(code.code);
        setDiscountPercent(code.discountPercent);
        setRevenueSharePercent(code.revenueSharePercent);
        setApplyResultMessage(code.applyResultMessage);
        setIsActive(code.isActive);
        setExpiresAt(code.expiresAt ? code.expiresAt.toISOString().split('T')[0] : '');
      } else {
        // Create mode - reset to defaults
        setFormCode('');
        setDiscountPercent(0);
        setRevenueSharePercent(10);
        setApplyResultMessage('Code {code} Applied');
        setIsActive(true);
        setExpiresAt('');
      }
      setError('');
    }
  }, [code, isOpen]);

  const validateForm = (): string | null => {
    if (!isEditMode) {
      // Validate code (only for create)
      const trimmedCode = formCode.trim();
      if (!trimmedCode) {
        return 'Code is required';
      }
      if (trimmedCode.length < 3 || trimmedCode.length > 20) {
        return 'Code must be between 3 and 20 characters';
      }
      if (!/^[A-Z0-9]+$/i.test(trimmedCode)) {
        return 'Code must contain only letters and numbers';
      }
    }

    // Validate discount percent
    if (discountPercent < 0 || discountPercent > 100) {
      return 'Discount percent must be between 0 and 100';
    }

    // Validate revenue share percent
    if (revenueSharePercent < 0 || revenueSharePercent > 100) {
      return 'Revenue share percent must be between 0 and 100';
    }

    // Validate apply result message
    if (!applyResultMessage.trim()) {
      return 'Apply result message is required';
    }

    // Validate expiration date if provided
    if (expiresAt) {
      const expirationDate = new Date(expiresAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (expirationDate < today) {
        return 'Expiration date must be in the future';
      }
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      let resultCode: ReferralCodeData;

      if (isEditMode && code) {
        // Update existing code
        const updateData: UpdateReferralCodeData = {
          discountPercent,
          isActive,
          revenueSharePercent,
          applyResultMessage: applyResultMessage.trim(),
          expiresAt: expiresAt ? new Date(expiresAt) : null,
        };
        resultCode = await referralCodesService.updateReferralCode(code.code, updateData);
      } else {
        // Create new code
        const createData: CreateReferralCodeData = {
          code: formCode.trim(),
          discountPercent,
          isActive,
          revenueSharePercent,
          applyResultMessage: applyResultMessage.trim(),
          expiresAt: expiresAt ? new Date(expiresAt) : null,
        };
        resultCode = await referralCodesService.createReferralCode(createData);
      }

      onSuccess(resultCode, isEditMode);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${isEditMode ? 'update' : 'create'} referral code`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Referral Code' : 'Create Referral Code'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}

        {/* Code Input */}
        <div>
          <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
            Code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="code"
            value={formCode}
            onChange={(e) => setFormCode(e.target.value.toUpperCase())}
            disabled={isEditMode}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed uppercase"
            placeholder="CODE"
            autoFocus={!isEditMode}
          />
          {!isEditMode && (
            <p className="mt-1 text-xs text-gray-500">
              3-20 characters, letters and numbers only
            </p>
          )}
        </div>

        {/* Discount and Revenue Share in Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="discountPercent" className="block text-sm font-medium text-gray-700 mb-1">
              Discount % <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              id="discountPercent"
              value={discountPercent}
              onChange={(e) => setDiscountPercent(Number(e.target.value))}
              min="0"
              max="100"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label htmlFor="revenueSharePercent" className="block text-sm font-medium text-gray-700 mb-1">
              Revenue Share % <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              id="revenueSharePercent"
              value={revenueSharePercent}
              onChange={(e) => setRevenueSharePercent(Number(e.target.value))}
              min="0"
              max="100"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Apply Result Message */}
        <div>
          <label htmlFor="applyResultMessage" className="block text-sm font-medium text-gray-700 mb-1">
            Apply Result Message <span className="text-red-500">*</span>
          </label>
          <textarea
            id="applyResultMessage"
            value={applyResultMessage}
            onChange={(e) => setApplyResultMessage(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Code DEUCE Added"
          />
          <p className="mt-1 text-xs text-gray-500">
            Message shown to users when code is successfully applied
          </p>
        </div>

        {/* Is Active Toggle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <div className="flex items-center gap-3">
            <div onClick={(e) => e.preventDefault()}>
              <Toggle checked={isActive} onChange={setIsActive} variant="light" />
            </div>
            <span className="text-sm text-gray-600">
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {/* Expires At */}
        <div>
          <label htmlFor="expiresAt" className="block text-sm font-medium text-gray-700 mb-1">
            Expiration Date (Optional)
          </label>
          <input
            type="date"
            id="expiresAt"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
          
          {/* Quick Select Buttons */}
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                const date = new Date();
                date.setMonth(date.getMonth() + 1);
                setExpiresAt(date.toISOString().split('T')[0]);
              }}
              className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
            >
              1 Month
            </button>
            <button
              type="button"
              onClick={() => {
                const date = new Date();
                date.setMonth(date.getMonth() + 3);
                setExpiresAt(date.toISOString().split('T')[0]);
              }}
              className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
            >
              3 Months
            </button>
            <button
              type="button"
              onClick={() => {
                const date = new Date();
                date.setMonth(date.getMonth() + 6);
                setExpiresAt(date.toISOString().split('T')[0]);
              }}
              className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
            >
              6 Months
            </button>
            <button
              type="button"
              onClick={() => {
                const date = new Date();
                date.setFullYear(date.getFullYear() + 1);
                setExpiresAt(date.toISOString().split('T')[0]);
              }}
              className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
            >
              1 Year
            </button>
            <button
              type="button"
              onClick={() => setExpiresAt('')}
              className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading}
          >
            {loading ? 'Saving...' : isEditMode ? 'Update Code' : 'Create Code'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
